<template>
  <v-card id="gift-rule-list">
    <v-card-text class="d-flex align-center flex-wrap pb-0">
      <div class="d-flex align-center pb-5">
        <v-btn
          color="primary"
          class="me-3"
          :disabled="hasNotPermission('Permissions.GiftRuleCreate')"
          @click="add"
        >
          <v-icon
            size="18"
            class="me-1"
          >
            {{ icons.mdiPlus }}
          </v-icon>
          <span>Добавить</span>
        </v-btn>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center pb-5">
        <v-text-field
          v-model="filters.searchQuery"
          single-line
          dense
          outlined
          hide-details
          placeholder="Поиск..."
          class="gift-rule-list-search me-3"
        ></v-text-field>
      </div>
    </v-card-text>
    <v-data-table
      :headers="headers"
      :items="rules"
      :server-items-length="totalCount"
      :loading="loading"
      :page.sync="filters.pageNumber"
      :items-per-page.sync="filters.pageSize"
      :sort-by.sync="filters.sortBy"
      :sort-desc.sync="filters.sortDesc"
      class="text-no-wrap"
    >
      <template #[`item.isEnabled`]="{item}">
        <v-icon
          v-if="item.isEnabled"
          size="20"
          color="success"
        >
          {{ icons.mdiCheck }}
        </v-icon>
      </template>
      <template #[`item.condition`]="{item}">
        {{ condition(item) }}
      </template>
      <template #[`item.startsAt`]="{item}">
        <span class="text-no-wrap">{{ toDate(item.startsAt) }}</span>
      </template>
      <template #[`item.endsAt`]="{item}">
        <span class="text-no-wrap">{{ item.endsAt ? toDate(item.endsAt) : 'бессрочно' }}</span>
      </template>
      <template #[`item.actions`]="{item}">
        <div class="d-flex align-center justify-center">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                :disabled="hasNotPermission('Permissions.GiftRuleUpdate')"
                v-bind="attrs"
                v-on="on"
                @click="edit(item.id)"
              >
                <v-icon size="18">
                  {{ icons.mdiPencilOutline }}
                </v-icon>
              </v-btn>
            </template>
            <span>Редактирование</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                :disabled="hasNotPermission('Permissions.GiftRuleDelete')"
                v-bind="attrs"
                v-on="on"
                @click="deletePopupShow(item.id, item.name)"
              >
                <v-icon size="18">
                  {{ icons.mdiDeleteOutline }}
                </v-icon>
              </v-btn>
            </template>
            <span>Удалить</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <gift-rule-form
      :show="formShown"
      :label="formLabel"
      :save="saveRule"
      :cancellation="closeForm"
    />

    <delete-dialog
      ref="deletePopup"
      :label="deleteLabel"
      @delete-confirmed="removeRule"
    />
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import {
  mdiPlus,
  mdiDeleteOutline,
  mdiPencilOutline,
  mdiCheck,
} from '@mdi/js'
import { mapFormHandlers } from '@/helpers/forms'
import DeleteDialog from '@/components/delete-dialog'
import GiftRuleForm from '@/views/gift-rule/gift-rule-form.vue'
import { hasPermission, toDateSting } from '@/helpers/index'

const CONDITIONS = {
  MenuCategory: 'категория меню',
  Dishes: 'список блюд',
  PresentProviders: 'блюда с признаком «даёт подарок»',
}

export default {
  name: 'GiftRuleList',
  components: { DeleteDialog, GiftRuleForm },
  data: () => ({
    icons: {
      mdiPlus,
      mdiPencilOutline,
      mdiDeleteOutline,
      mdiCheck,
    },
    deleteLabel: '',
    formShown: false,
    formLabel: '',
  }),
  computed: {
    ...mapFormHandlers('giftRule', [
      'filters.pageNumber',
      'filters.pageSize',
      'filters.searchQuery',
      'filters.sortBy',
      'filters.sortDesc',
    ]),
    ...mapState('giftRule', { rules: 'list', totalCount: 'totalCount', loading: 'loading' }),
    headers() {
      return [
        { text: 'Название', value: 'name' },
        { text: 'Включено', value: 'isEnabled' },
        { text: 'Условие', value: 'condition', sortable: false },
        { text: 'Нужно блюд', value: 'triggerQuantity' },
        { text: 'Подарков', value: 'giftQuantity' },
        { text: 'Действует с', value: 'startsAt' },
        { text: 'По', value: 'endsAt' },
        {
          text: 'Действия',
          value: 'actions',
          align: 'center',
          sortable: false,
        },
      ]
    },
  },
  async created() {
    document.title = 'Подарки за состав'
    this.fetchRules()

    this.fetchOptions()
  },
  methods: {
    ...mapActions('giftRule', { fetchRules: 'fetchList', deleteRule: 'delete' }),
    ...mapActions('giftRule/form', ['fetchOptions', 'fetchById', 'resetForm', 'create', 'update']),
    toDate(value) {
      return toDateSting(value)
    },
    hasNotPermission(value) {
      return !hasPermission(value)
    },
    condition(item) {
      return CONDITIONS[item.triggerKind] ?? ''
    },
    add() {
      this.resetForm()
      this.formLabel = 'Новое правило подарка'
      this.formShown = true
    },
    async edit(id) {
      await this.fetchById(id)
      this.formLabel = 'Правило подарка'
      this.formShown = true
    },
    closeForm() {
      this.formShown = false
    },
    async saveRule() {
      const { id } = this.$store.state.giftRule.form

      await (id ? this.update() : this.create())

      this.formShown = false
      this.fetchRules()
    },
    deletePopupShow(id, name) {
      this.deleteLabel = `Правило «${name}» будет удалено.`
      this.$refs.deletePopup.open(id)
    },
    removeRule(id) {
      return this.deleteRule(id)
    },
  },
}
</script>

<style lang="scss" scoped>
#gift-rule-list {
  .gift-rule-list-search {
    max-width: 10.625rem;
  }
}
</style>
